import type { CoachingPartnershipMessage } from '@efdigital/ll-addon-data';
import { DataTypeCoaching } from '@efdigital/ll-addon-data/data-types';
import { cacheKeyFor, type Store } from '@warp-drive/core';
import {
  createRecord,
  query,
  updateRecord,
} from '@warp-drive/utilities/json-api';

import { jsonApiOptions, page } from '../../json-api.ts';

/**
 * A deliberately bounded page prevents an accidental unbounded thread request.
 * Threads are sorted oldest-first so callers can render the response directly.
 */
export const MAX_MESSAGES_PER_PARTNERSHIP = 1000;

export function queryPartnershipMessages(coachingPartnershipId: string) {
  return query<CoachingPartnershipMessage>(
    DataTypeCoaching.CoachingPartnershipMessage,
    {
      'filter[coachingPartnership.id]': coachingPartnershipId,
      ...page({ offset: 0, limit: MAX_MESSAGES_PER_PARTNERSHIP }),
      'sort[]': 'createdAt',
    },
    jsonApiOptions
  );
}

export function createPartnershipMessage(
  message: CoachingPartnershipMessage,
  store: Store
) {
  const init = createRecord(message, jsonApiOptions);
  init.body = JSON.stringify({
    data: store.cache.peek(cacheKeyFor(message)),
  });
  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}

/**
 * The API rejects a PATCH carrying anything but the caller's own receipt, so
 * the body is narrowed to that one field rather than the whole cached record.
 */
export function updatePartnershipMessageReadReceipt(
  message: CoachingPartnershipMessage,
  field: 'readByCoachAt' | 'readByCoacheeAt',
  store: Store
) {
  const init = updateRecord(message, { ...jsonApiOptions, patch: true });
  const cached = store.cache.peek(cacheKeyFor(message)) as {
    type: string;
    id: string | null;
  };

  init.body = JSON.stringify({
    data: {
      type: cached.type,
      id: cached.id,
      attributes: { [field]: message[field] },
    },
  });
  init.headers.set('Content-Type', 'application/vnd.api+json');
  return init;
}
